import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ActivityDto, AlertDto } from '../../shared/api';
import { AREA_TYPES, FREE_STOCK, TYPES, typeLabel, type AreaType } from '../../shared/domain';
import { get, post, refresh } from '../api';
import { ActTable, Bar, Err, ItemLink, QcTag, Rich, ShelfDraw, Spinner, Tag, Where, errMsg, useApp } from '../components/ui';
import { Dialog } from '../components/cards';

export function Today() {
  const { d, go, openItem, openArticle, toast } = useApp();
  const al = useQuery({ queryKey: ['alerts'], queryFn: () => get<AlertDto[]>('/api/alerts') });
  const acts = useQuery({ queryKey: ['acts', 'today'], queryFn: () => get<ActivityDto[]>('/api/activities?limit=8') });
  const [busy, setBusy] = useState(false);
  const out = d.b.items.filter((i) => i.status === 'out');
  const decide = async (id: number, approve: boolean) => {
    setBusy(true);
    try {
      const r = await post<{ text: string }>(`/api/requests/${id}/decide`, { approve });
      toast(r.text);
      await refresh();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const a = al.data ?? [];
  return (
    <>
      <h1>Today</h1>
      <p className="sub">What needs attention now.</p>
      <h2>Needs attention ({al.isLoading ? '…' : a.length})</h2>
      {al.isLoading ? (
        <Spinner />
      ) : a.length ? (
        a.map((x, k) => (
          <div key={k} className={`alert ${x.bad ? 'bad' : ''}`}>
            <span>
              <Rich t={x.t} />
            </span>
            <span className="row" style={{ marginLeft: 'auto', flexWrap: 'nowrap' }}>
              {x.req ? (
                <>
                  <button className="btn small primary" disabled={busy} onClick={() => void decide(x.req!, true)}>
                    Approve
                  </button>
                  <button className="btn small" disabled={busy} onClick={() => void decide(x.req!, false)}>
                    Reject
                  </button>
                </>
              ) : (
                <button className="btn small" onClick={() => (x.page ? go('/' + x.page) : x.art ? openArticle(x.art) : x.no && openItem(x.no))}>
                  Open
                </button>
              )}
            </span>
          </div>
        ))
      ) : (
        <p className="meta">Nothing to do.</p>
      )}
      <h2>Taken out right now ({out.length})</h2>
      <div className="panel">
        {out.length ? (
          out.map((i) => (
            <div key={i.no} className="row" style={{ padding: '5px 0' }}>
              <ItemLink no={i.no} />
              {d.artOf(i).name} <Where i={i} />
            </div>
          ))
        ) : (
          <span className="meta">All items are on the shelves.</span>
        )}
      </div>
      <h2>Latest activities</h2>
      <ActTable list={acts.data ?? []} compact />
    </>
  );
}

export function Items() {
  const { d, openItem, go } = useApp();
  const [type, setType] = useState('');
  const [filt, setFilt] = useState('all');
  const [q, setQ] = useState('');
  const ql = q.toLowerCase();
  const list = d.b.items.filter((i) => {
    const a = d.artOf(i);
    if (type === 'cons' && !a.consumable) return false;
    if (type === '3' && !(i.status === 'in' && d.isRepair(d.shelf(i.loc?.shelf)))) return false;
    if (type && !['cons', '3'].includes(type) && a.type !== type) return false;
    if (filt === 'all' && i.status === 'gone') return false;
    if (filt === 'in' && i.status !== 'in') return false;
    if (filt === 'out' && i.status !== 'out') return false;
    if (filt === 'gone' && i.status !== 'gone') return false;
    if (filt === 'res' && !d.reserved(i)) return false;
    if (filt === 'qc') {
      const s = d.qcOf(i);
      if (i.status === 'gone' || (s.ok && s.k !== 'soon')) return false;
    }
    return !ql || `${i.no} ${a.name} ${i.serial}`.toLowerCase().includes(ql);
  });
  return (
    <>
      <h1>Items</h1>
      <p className="sub">
        One line per numbered item (a piece, or a box with a quantity). The number never changes and never shows the place. To see how many we have of one thing, use{' '}
        <button className="linkbtn" onClick={() => go('/articles')}>
          Articles
        </button>
        .
      </p>
      <div className="row" style={{ marginBottom: 8 }}>
        {[
          ['', 'All storage types'],
          ['1-1', '1-1 Workshop'],
          ['1-2', '1-2 Installation'],
          ['2-1', '2-1 Products'],
          ['2-2', '2-2 Pieces'],
          ['2-3', '2-3 Amount'],
          ['cons', 'Consumables'],
          ['3', 'In R1'],
        ].map(([k, l]) => (
          <button key={k} className={`btn small ${type === k ? 'on' : ''}`} onClick={() => setType(k!)}>
            {l}
          </button>
        ))}
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        {[
          ['all', 'Active'],
          ['in', 'On shelves'],
          ['out', 'Taken out'],
          ['res', 'Reserved'],
          ['qc', 'QC to do'],
          ['gone', 'Lost / thrown away'],
        ].map(([k, l]) => (
          <button key={k} className={`btn small ${filt === k ? 'on' : ''}`} onClick={() => setFilt(k!)}>
            {l}
          </button>
        ))}
        <input placeholder="Number, article or serial" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginLeft: 'auto', minWidth: 200 }} />
      </div>
      <div className="panel scroll" style={{ padding: '4px 8px' }}>
        <table>
          <thead>
            <tr>
              <th>Item number</th>
              <th>Article</th>
              <th>Bought for / now used for</th>
              <th>Where</th>
              <th>QC</th>
              <th>Qty</th>
            </tr>
          </thead>
          <tbody>
            {list.length ? (
              list.map((i) => (
                <tr key={i.no} className="click" onClick={() => openItem(i.no)}>
                  <td className="code" style={{ whiteSpace: 'nowrap' }}>
                    {i.no}
                  </td>
                  <td>
                    {d.artOf(i).name}
                    <br />
                    <span className="meta">{d.artOf(i).type}</span>
                  </td>
                  <td>
                    {i.proj ? (
                      <>
                        {i.proj}
                        {d.usedFor(i) !== i.proj && (
                          <>
                            {' → '}
                            <b>{d.usedFor(i)}</b>
                          </>
                        )}
                      </>
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </td>
                  <td>
                    {i.status === 'in' ? (
                      <>
                        <span className="code">{d.addr(i.loc)}</span>
                        {i.repair && (
                          <>
                            {' '}
                            <Tag cls="warn">{i.repair}</Tag>
                          </>
                        )}
                      </>
                    ) : i.status === 'gone' ? (
                      <Tag cls="bad">{i.gone?.reason}</Tag>
                    ) : (
                      <Tag cls="warn">Taken out</Tag>
                    )}
                  </td>
                  <td>{i.status !== 'gone' && <QcTag i={i} />}</td>
                  <td>{d.fmtQty(i)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="meta">
                  No items match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="meta">New items get their number in Scan, then Register.</p>
    </>
  );
}

export function Articles() {
  const { d, openArticle } = useApp();
  const [q, setQ] = useState('');
  const ql = q.toLowerCase();
  const list = d.b.articles.filter((a) => !ql || `${a.name} ${a.specs} ${a.supplier}`.toLowerCase().includes(ql));
  return (
    <>
      <h1>Articles</h1>
      <p className="sub">An article is the kind of thing, for example "Syslogic edge processor v2". It holds the size, details and supplier info. Search an article to see how many we have, where each one is, and which are taken out.</p>
      <input placeholder="Search article, specs or supplier" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 280, marginBottom: 12 }} />
      <div className="panel scroll" style={{ padding: '4px 8px' }}>
        <table>
          <thead>
            <tr>
              <th>Article</th>
              <th>Storage type</th>
              <th>On shelves</th>
              <th>Taken out</th>
              <th>Minimum</th>
              <th>Places</th>
            </tr>
          </thead>
          <tbody>
            {list.map((a) => {
              const its = d.b.items.filter((i) => i.articleId === a.id && i.status !== 'gone');
              const ins = its.filter((i) => i.status === 'in');
              const st = d.artStock(a);
              const outN = its.filter((i) => i.status === 'out').length;
              return (
                <tr key={a.id} className="click" onClick={() => openArticle(a.id)}>
                  <td>
                    <b>{a.name}</b>
                    {a.linkExpired && (
                      <>
                        {' '}
                        <Tag cls="bad">link expired</Tag>
                      </>
                    )}
                  </td>
                  <td className="meta">{typeLabel(a)}</td>
                  <td>
                    {a.stock ? (
                      <>
                        {+st.toFixed(2)} {a.unit}{' '}
                        <span className="meta">
                          ({ins.length} box{ins.length === 1 ? '' : 'es'})
                        </span>
                        <Bar q={st} min={a.min} />
                      </>
                    ) : (
                      `${ins.length} pc`
                    )}
                  </td>
                  <td>{outN || <span className="meta">0</span>}</td>
                  <td>
                    {a.stock ? (
                      a.min ? (
                        <>
                          {a.min} {a.unit}
                          {d.artLow(a) && (
                            <>
                              {' '}
                              <Tag cls="bad">reorder</Tag>
                            </>
                          )}
                        </>
                      ) : (
                        <Tag cls="warn">not set</Tag>
                      )
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </td>
                  <td className="code">{[...new Set(ins.map((i) => d.addr(i.loc)))].join(', ')}</td>
                </tr>
              );
            })}
            {!list.length && (
              <tr>
                <td colSpan={6} className="meta">
                  No articles match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function Projects() {
  const { d, toast } = useApp();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sp, setSp] = useState(d.b.suitcaseProject);
  const [err, setErr] = useState('');
  const save = async (fn: () => Promise<unknown>, msg: string) => {
    setErr('');
    try {
      await fn();
      await refresh();
      toast(msg);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    }
  };
  return (
    <>
      <h1>Projects</h1>
      <p className="sub">Project codes are part of the long item numbers. Items with a project code are reserved for that project, except {FREE_STOCK} General (inventory), which is free stock. R&D codes start with 02, project codes with 03.</p>
      <div className="panel scroll" style={{ padding: '4px 8px' }}>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Kind</th>
              <th>Items bought for it</th>
              <th>Items now used for it</th>
            </tr>
          </thead>
          <tbody>
            {d.b.projects.map((p) => {
              const bf = d.b.items.filter((i) => i.proj === p.code && i.status !== 'gone');
              const nf = d.b.items.filter((i) => d.usedFor(i) === p.code && i.proj !== p.code && i.status !== 'gone');
              return (
                <tr key={p.code}>
                  <td className="code">{p.code}</td>
                  <td>{p.name}</td>
                  <td className="meta">
                    {p.code.startsWith('02') ? 'R&D' : 'Project'}
                    {p.code === FREE_STOCK ? ', free stock' : ''}
                  </td>
                  <td>{bf.length ? bf.map((i, k) => <span key={i.no}>{k > 0 && ', '}<ItemLink no={i.no} /></span>) : <span className="meta">0</span>}</td>
                  <td>{nf.length ? nf.map((i, k) => <span key={i.no}>{k > 0 && ', '}<ItemLink no={i.no} /></span>) : <span className="meta">same</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <h2>Installation suitcase project</h2>
      <div className="panel">
        {d.isOwner ? (
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: 260 }}>
              4-digit code of the installation suitcase project
              <input value={sp} placeholder="still to be added" maxLength={4} onChange={(e) => setSp(e.target.value)} />
            </label>
            <button className="btn" onClick={() => void save(() => post('/api/settings/suitcase-project', { code: sp }), 'Saved')}>
              Save
            </button>
          </div>
        ) : (
          <span>{d.b.suitcaseProject || 'Code still to be added.'}</span>
        )}
        <p className="meta" style={{ margin: '8px 0 0' }}>
          The suitcase tools keep their 07 tool numbers. This code shows what they are used for.
        </p>
      </div>
      {d.isOwner && (
        <>
          <h2>Add a project or R&D code</h2>
          <div className="panel row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ width: 110 }}>
              Code
              <input maxLength={4} placeholder="0325" value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            <label className="field" style={{ minWidth: 220 }}>
              Name
              <input placeholder="Project name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <button
              className="btn primary"
              onClick={async () => {
                if (await save(() => post('/api/projects', { code, name }), `${code} added`)) {
                  setCode('');
                  setName('');
                }
              }}
            >
              Add code
            </button>
          </div>
        </>
      )}
      <Err e={err} />
    </>
  );
}

export function Shelves() {
  const { d, toast, openItem } = useApp();
  const [f, setF] = useState({ code: '', name: '', place: 'Office', areaType: '2-2' as AreaType, levels: 4 });
  const [err, setErr] = useState('');
  const [spot, setSpot] = useState<{ code: string; l: number; p: string } | null>(null);
  const add = async () => {
    setErr('');
    try {
      await post('/api/shelves', f);
      await refresh();
      toast(`Area ${f.code.toUpperCase()} added. Print its QR sticker.`);
      setF({ ...f, code: '', name: '' });
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  const spotItems = spot ? d.itemsAt(spot.code, spot.l, spot.p) : [];
  return (
    <>
      <h1>Storage areas</h1>
      <p className="sub">
        Each storage area has its own QR sticker. Items have no QR, only a text label with their number. A place inside an area is written as area, level and side: <b className="code">S1-2-L</b> = area S1, level 2, left. Level 1 is the bottom. An area can be a whole shelf or part of one.
      </p>
      <div className="panel scroll" style={{ padding: '4px 8px', marginBottom: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Place</th>
              <th>What is stored</th>
              <th>Items now</th>
            </tr>
          </thead>
          <tbody>
            {d.b.shelves.map((s) => (
              <tr key={s.code}>
                <td className="code" style={{ fontSize: 18 }}>
                  {s.code}
                </td>
                <td>
                  {s.name} <span className="meta">({s.place})</span>
                </td>
                <td>{s.what}</td>
                <td>{d.b.items.filter((i) => i.status === 'in' && i.loc?.shelf === s.code).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))' }}>
        {d.b.shelves.map((sh) => (
          <div key={sh.code}>
            <div className="shelfhead">
              <div>
                <h3 style={{ margin: 0 }}>
                  <span className="code" style={{ fontSize: 22 }}>
                    {sh.code}
                  </span>{' '}
                  {sh.name}
                </h3>
                <span className="meta">
                  {sh.place}, {sh.levels} levels
                </span>
              </div>
              <Tag cls={d.isRepair(sh) ? 'warn' : d.isLocked(sh) ? 'soft' : 'ok'}>{sh.areaType}</Tag>
            </div>
            <ShelfDraw
              sh={sh}
              onOpen={(l, p) => {
                const its = d.itemsAt(sh.code, l, p);
                if (its.length === 1) openItem(its[0]!.no);
                else setSpot({ code: sh.code, l, p });
              }}
            />
          </div>
        ))}
      </div>
      {spot && (
        <Dialog title={`${spot.code}-${spot.l}-${spot.p}`} onClose={() => setSpot(null)}>
          {spotItems.map((i) => (
            <div key={i.no} className="row" style={{ padding: '6px 0' }}>
              <button
                className="linkbtn code"
                onClick={() => {
                  setSpot(null);
                  openItem(i.no);
                }}
              >
                {i.no}
              </button>
              {d.artOf(i).name}
              <QcTag i={i} />
            </div>
          ))}
        </Dialog>
      )}
      {d.isOwner && (
        <>
          <h2>Add a storage area</h2>
          <div className="panel row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ width: 80 }}>
              Code
              <input placeholder="S4" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
            </label>
            <label className="field">
              Place
              <input placeholder="IKEA shelf 3" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </label>
            <label className="field">
              Room
              <input placeholder="Office" size={10} value={f.place} onChange={(e) => setF({ ...f, place: e.target.value })} />
            </label>
            <label className="field">
              What is stored
              <select value={f.areaType} onChange={(e) => setF({ ...f, areaType: e.target.value as AreaType })}>
                {AREA_TYPES.map((k) => (
                  <option key={k} value={k}>
                    {k} {k === '3' ? 'Repair / unused' : TYPES[k].name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Levels
              <input type="number" min={1} max={9} value={f.levels} style={{ width: 80 }} onChange={(e) => setF({ ...f, levels: parseInt(e.target.value, 10) || 1 })} />
            </label>
            <button className="btn primary" onClick={() => void add()}>
              Add area
            </button>
          </div>
          <Err e={err} />
        </>
      )}
    </>
  );
}
